import { useConfig } from 'config/use-config';
import { useAuth } from 'contexts/auth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAsyncFn } from 'react-use';
import { AsyncFnReturn } from 'react-use/lib/useAsyncFn';
import { isAxiosError } from 'utils/axios';
import {
  RedeemLoyaltyForGiftsResult,
  getLoyaltySummary,
  redeemLoyaltyPointForGift,
  redeemLoyaltyPoints,
} from '../../../../api/loyalty-program';
import { LoyaltySummaryResult } from '../../../../interfaces/loyalty-program';
import { CURRENCY_NO_DECIMALS } from 'constants/formatters';

export interface PointState {
  activePoints: LoyaltySummaryResult['activePoints'];
  creditPerRedeem: LoyaltySummaryResult['creditPerRedeem'];
  creditPerRedeemFairvalue: LoyaltySummaryResult['creditPerRedeemFairvalue'];
  formattedCreditPerRedeem: string;
  formattedCreditPerRedeemFairvalue: string;
  nextExpiringPoints?: LoyaltySummaryResult['nextExpiringPoints'];
  pointsPerRedeem: LoyaltySummaryResult['pointsPerRedeem'];
  pointsPerRedeemFairvalue: LoyaltySummaryResult['pointsPerRedeemFairvalue'];
  redeemErrors: Array<string>;
  redeemLoading: boolean;
  /** Redeem points state */
  redeemLoyaltyPointForGiftState: AsyncFnReturn<
    (points: number, variantId: number) => Promise<RedeemLoyaltyForGiftsResult>
  >[0];
  summaryErrors: Array<string>;
  summaryLoading: boolean;
  transactions: LoyaltySummaryResult['transactions'];
  getLoyaltySummary: () => Promise<void>;
  /** Redeem points into a single gift */
  redeemLoyaltyPointForGift: (
    points: number,
    variantId: number
  ) => Promise<RedeemLoyaltyForGiftsResult>;
  redeemPoint: (arg: { isFairvalue?: boolean; points: number }) => Promise<void>;
}

export const usePointsState = (): PointState => {
  const { currencyCode } = useConfig();
  const { customer, isUserLoggedIn } = useAuth();
  const customerId = customer?.customerId;
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [redeemLoading, setRedeemLoading] = useState<boolean>(false);
  const [summaryErrors, setSummaryErrors] = useState<Array<string>>([]);
  const [redeemErrors, setRedeemErrors] = useState<Array<string>>([]);
  const [loyaltyPointSummary, setLoyaltyPointSummary] = useState<LoyaltySummaryResult | null>(null);

  const intl = useIntl();

  const redeemPoint: PointState['redeemPoint'] = useCallback(
    async ({ isFairvalue, points }): Promise<void> => {
      if (!customerId) {
        return;
      }
      setRedeemLoading(true);
      try {
        await redeemLoyaltyPoints({
          customerId,
          isFairvalue,
          points,
        });
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response?.status === 409) {
            console.error(error, 'redeemLoyaltyPoints : insufficient');
            setRedeemErrors(['error:account:bonus-program:insufficient']);
            throw error;
          }
        }
        console.error(error, 'redeemLoyaltyPoints');
        setRedeemErrors(['error:unknown']);
        throw error;
      } finally {
        setRedeemLoading(false);
      }
    },
    [customerId]
  );

  const [redeemLoyaltyForGiftState, _redeemLoyaltyPointForGift] = useAsyncFn(
    async (points: number, variantId: number): Promise<RedeemLoyaltyForGiftsResult> => {
      if (!customerId) {
        return Promise.reject();
      }
      try {
        return await redeemLoyaltyPointForGift({
          items: [
            {
              variantId,
              quantity: 1,
              points,
            },
          ],
          customerId,
        });
      } catch (error) {
        console.error(error);
        setRedeemErrors(['error:unknown']);
        throw error;
      }
    },
    [customerId, setRedeemLoading, setRedeemErrors]
  );

  const _getLoyaltySummary = useCallback(async () => {
    setSummaryErrors([]);
    setSummaryLoading(true);
    try {
      if (!customerId) {
        return;
      }
      const res = await getLoyaltySummary({ customerId });
      setLoyaltyPointSummary(res);
    } catch (error) {
      console.error(error, 'getLoyaltySummary');
      setSummaryErrors(['error:unknown']);
      throw error;
    } finally {
      setSummaryLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    isUserLoggedIn && _getLoyaltySummary();
  }, [_getLoyaltySummary, isUserLoggedIn]);

  const creditPerRedeem = loyaltyPointSummary?.creditPerRedeem || 0;
  const creditPerRedeemFairvalue = loyaltyPointSummary?.creditPerRedeemFairvalue || 0;

  const formatCredit = useCallback(
    (amount: number) => {
      return intl.formatNumber(amount, {
        currency: currencyCode,
        ...CURRENCY_NO_DECIMALS,
      });
    },
    [intl]
  );

  const formattedCreditPerRedeem = useMemo(
    () => formatCredit(creditPerRedeem),
    [formatCredit, creditPerRedeem]
  );

  const formattedCreditPerRedeemFairvalue = useMemo(
    () => formatCredit(creditPerRedeemFairvalue),
    [formatCredit, creditPerRedeemFairvalue]
  );

  return {
    activePoints: loyaltyPointSummary?.activePoints || 0,
    creditPerRedeem,
    creditPerRedeemFairvalue,
    formattedCreditPerRedeem,
    formattedCreditPerRedeemFairvalue,
    nextExpiringPoints: loyaltyPointSummary?.nextExpiringPoints,
    pointsPerRedeem: loyaltyPointSummary?.pointsPerRedeem || Number.POSITIVE_INFINITY,
    pointsPerRedeemFairvalue:
      loyaltyPointSummary?.pointsPerRedeemFairvalue || Number.POSITIVE_INFINITY,
    redeemErrors,
    redeemLoading,
    redeemLoyaltyPointForGiftState: redeemLoyaltyForGiftState,
    summaryErrors,
    summaryLoading,
    transactions: loyaltyPointSummary?.transactions || [],
    getLoyaltySummary: _getLoyaltySummary,
    redeemLoyaltyPointForGift: _redeemLoyaltyPointForGift,
    redeemPoint,
  };
};
